/**
 * What a person owes today.
 *
 * Jonathon, 2026-09-02: "now please plan and build out a todo list for each
 * rep. i'm open to ideas."
 *
 * The idea is that almost none of it should be typed. Roof Board already knows
 * that nobody has rung this customer in two days, that a proposal went out on
 * Tuesday and has been opened twice without being signed, that a job is in
 * production with nobody booked to do the work, and that somebody tagged you in
 * a note this morning. Every one of those was already in the database and none
 * of them was ever gathered up, so "what do I owe today" lived in a rep's head
 * and fell out of it.
 *
 * So this module reads the data the app has already fetched and says what is
 * owed. It is pure — nothing here queries anything, and `now` comes in as an
 * argument so the tests do not depend on the clock. The web layer hands it what
 * it has and draws the answer.
 *
 * ## Every item has a key
 *
 * A computed item cannot simply be ticked off, because ticking "chase the Hurst
 * proposal" does not sign the proposal, and on the next render the item would
 * be back. So every item carries a `key` built from the thing it is about — a
 * proposal's id, a job's id — and `public.tasks` records what a person did about
 * that key. Suppression is by key, and the key changes when the underlying
 * thing changes, so a re-sent proposal is a new item and gets chased again.
 *
 * ## What is deliberately not here
 *
 * Three signals worth having need a query heavy enough that they belong in a
 * second round rather than being smuggled in cheaply:
 *
 *   - **Colours missing before an order can be placed.** `missingColors` in
 *     colorRules.ts already decides it, but it needs every order's lines joined
 *     to the product catalogue.
 *   - **An inbound text or call nobody answered.** Email arrived first, with
 *     ADR 0158: a reply is read by Jev and becomes a task below. Texts and
 *     calls still need their own reading before they can join it.
 *   - **A deposit owed.** `productionColumn` in production.ts already decides
 *     it, from that job's checklist rows — which means fetching the checklist
 *     for every sold job.
 */

import { followUpStatus, humanGap, type FollowUpInput } from "./followup.js";
import { cents, formatMoney } from "./money.js";
import { isTestJob } from "./lost.js";
import { replyCertainty, type StoredReplyMeaning } from "./replyReading.js";
import { localDateString } from "./signedDate.js";
import { SOLD_STAGES } from "./stage.js";
import type { JobStage } from "./types.js";
import type { UnsignedWork } from "./unsignedWork.js";
import {
  paymentTermsWarning,
  type PaymentTermsCheckRow,
} from "./paymentTermsCheck.js";

/**
 * The kinds of thing the app works out for itself.
 *
 * Grouped by the four sorts of trouble Jonathon asked for: a call or a reply
 * owed, money and paperwork, a date coming up, and data that is plainly wrong.
 */
export type TaskKind =
  // What a customer said (ADR 0158)
  | "customer_urgent"
  | "customer_accepted"
  | "reply_owed"
  | "supplier_reply"
  | "customer_paid"
  // Calls and replies owed
  | "call_overdue"
  | "follow_up_due"
  | "tagged_in_note"
  // Money and paperwork
  | "proposal_unsigned"
  | "work_unsigned"
  | "terms_not_in_contract"
  | "order_not_placed"
  | "materials_unconfirmed"
  | "invoice_not_sent"
  | "receipt_to_send"
  | "plan_payment_late"
  // Supplier mail to orders@ (ADR 0311)
  | "supplier_mail_review"
  | "supplier_credit_memo"
  // Dates and jobs
  | "inspection_soon"
  | "no_crew_booked"
  // Data that is wrong
  | "sold_without_signed_date"
  | "signed_but_wrong_stage";

/**
 * How loudly each kind asks.
 *
 * Lower sorts first. The order is not severity in the abstract, it is what a
 * rep should do with the next ten minutes: a customer waiting on a human being
 * comes before paperwork, and paperwork that is somebody else's money comes
 * before a tidy-up. Data problems sit last because nobody is waiting on them —
 * they are real, and they will still be real this afternoon.
 */
const WEIGHT: Record<TaskKind, number> = {
  // A customer who wrote to say something is wrong, or to ask for a call, is
  // the one person on this list already waiting on a human being.
  customer_urgent: 5,
  call_overdue: 10,
  customer_accepted: 15,
  follow_up_due: 20,
  reply_owed: 25,
  supplier_reply: 58,
  inspection_soon: 30,
  tagged_in_note: 40,
  proposal_unsigned: 50,
  // Just below chasing a signature, because it is one: except that the work
  // is already under way and nobody asked for the signature (issue #463).
  work_unsigned: 52,
  // Beside it: paperwork that disagrees with what the customer was told
  // (ADR 0226). Somebody should fix it before the customer notices.
  terms_not_in_contract: 53,
  customer_paid: 55,
  // A monthly check on an in-house payment plan that has not come (ADR 0286).
  // Money the company is owed, and nobody else is watching for it.
  plan_payment_late: 56,
  // A supplier's email nobody could place, or a credit memo whose return
  // has not been entered (ADR 0311): paperwork, beside the supplier reply.
  supplier_mail_review: 59,
  supplier_credit_memo: 59,
  order_not_placed: 60,
  // Below a known unplaced order: this one is a question, not a known miss.
  materials_unconfirmed: 65,
  no_crew_booked: 70,
  invoice_not_sent: 80,
  // Above the tidying-up items and below the invoice: a customer who handed
  // over a check is waiting to hear that it arrived, and nobody else will tell
  // them.
  receipt_to_send: 85,
  sold_without_signed_date: 90,
  signed_but_wrong_stage: 100,
};

export interface DerivedTask {
  /** Stable across renders, and the handle `public.tasks` suppresses it by. */
  key: string;
  kind: TaskKind;
  /** What to do, as the rep reads it. */
  title: string;
  /** Why it is being asked. One quiet line under the title. */
  detail: string;
  jobId: string | null;
  /**
   * When it is owed, as a plain calendar date, or null for "when you can".
   * Something already overdue carries the date it fell due, not today.
   */
  dueOn: string | null;
  weight: number;
}

/** A job, as much of one as this module reads. */
export interface TaskJob extends FollowUpInput {
  id: string;
  job_number?: number | null;
  stage: JobStage;
  is_test?: boolean | null;
  rep_id?: string | null;
  alt_rep_id?: string | null;
  pm_id?: string | null;
  inspector_id?: string | null;
  inspector_2_id?: string | null;
  coordinator_id?: string | null;
  sends_at?: string | null;
  signed_contract_at?: string | null;
  inspection_at?: string | null;
  contact?: { full_name?: string | null } | null;
}

/** A proposal that has been sent to a customer. */
export interface TaskProposal {
  id: string;
  job_id: string;
  status: string;
  sent_at: string | null;
  viewed_at?: string | null;
}

/** A note that tagged somebody. */
export interface TaskMention {
  id: string;
  job_id: string | null;
  body: string | null;
  occurred_at: string;
  mentions: readonly string[] | null;
  author?: { full_name?: string | null } | null;
}

/** A material order, only as far as whether it has been placed. */
export interface TaskMaterialOrder {
  id: string;
  job_id: string;
  order_number?: number | null;
  ordered_at: string | null;
  hold_order?: boolean | null;
  /**
   * `not_placed`, `ordered`, `picked_up` or `paid`. Anything past `not_placed`
   * was placed, whether or not anybody stamped `ordered_at`: on production on
   * 2026-09-18, eight picked-up or paid orders had no `ordered_at` at all.
   */
  status?: string | null;
  /** False for an order on an estimate that is not the active one. */
  is_live?: boolean | null;
}

/** A work order, only as far as whether anybody is booked. */
export interface TaskWorkOrder {
  id: string;
  job_id: string;
  scheduled_at: string | null;
  is_live?: boolean | null;
  /** When the crew starts, which is when the material has to be there. */
  start_at?: string | null;
}

/**
 * A job's "Material" checklist step. `done` or `na` means nobody needs to
 * order anything: it was handled, or it came off the shelf or with the crew —
 * the database marks it `na` itself when either of those switches is on
 * (ADR 0102).
 */
export interface TaskMaterialStep {
  job_id: string;
  status: string;
}

/** An invoice that has been prepared. */
export interface TaskInvoice {
  id: string;
  job_id: string;
  status: string;
}

/**
 * A customer's reply and what Jev read it as (ADR 0158): a `message_readings`
 * row, flattened with the message and thread it is about.
 */
export interface TaskReading {
  /** The `email_messages` id, and what the task is keyed by. */
  message_id: string;
  thread_id: string;
  job_id: string | null;
  meaning: StoredReplyMeaning;
  probability: number;
  sender_kind?:
    "customer" | "supplier" | "staff" | "automated" | "unknown" | undefined;
  sent_at: string;
  from_name?: string | null | undefined;
  from_address?: string | null | undefined;
  snippet?: string | null | undefined;
  /**
   * The `Message-ID:` header. The same customer email copied to two staff
   * mailboxes is two rows with one of these, and is one task (ADR 0162).
   */
  rfc822_message_id?: string | null | undefined;
  /**
   * Somebody dismissed it on the AI Team page, as wrong or as right but fine
   * (ADR 0259). It makes no task.
   */
  dismissed?: boolean | undefined;
}

/** One email we sent, as far as whether it answered a reply. */
export interface TaskOutbound {
  thread_id: string;
  /** The job its thread belongs to. */
  job_id?: string | null;
  /** Who it went to, To and Cc together. */
  to_addresses?: readonly string[] | null;
  sent_at: string;
}

/** A call somebody logged on a job, in or out. */
export interface TaskCall {
  job_id: string;
  occurred_at: string;
}

/**
 * Money recorded against a job, from any door: a check or cash handed over,
 * a Mercury invoice started or paid, a QuickBooks payment. `at` is when the
 * app learned of it.
 */
export interface TaskPayment {
  job_id: string;
  at: string;
}

/** A receipt drawn up and waiting for somebody to read it (ADR 0140). */
export interface TaskReceipt {
  id: string;
  job_id: string;
  /** For the line the rep reads: "$1,100.00 check". */
  amount_cents: number;
  method: string;
  receipt_waiting_since: string;
}

/**
 * A customer on an in-house payment plan whose monthly payment is late
 * (ADR 0286), already worked out by `jobPlanStanding`.
 */
export interface TaskLatePlan {
  job_id: string;
  /** What should have arrived by now and has not. */
  behind_cents: number;
  /** The due date of the oldest payment not made. */
  overdue_since: string;
}

/**
 * An email a supplier sent to orders@, as `supplier_mail` holds it
 * (ADR 0311). `review` rows wait for a person; a filed credit memo waits for
 * its return to be entered.
 */
export interface TaskSupplierMail {
  id: string;
  status: "filed" | "review" | "ignored";
  kind: "acknowledgement" | "invoice" | "credit_memo" | "unknown";
  job_id: string | null;
  material_order_id?: string | null;
  order_number?: number | null;
  subject: string | null;
  from_name?: string | null;
  from_address?: string | null;
  sent_at: string;
  /** Why it waits, as `supplierMailReviewSentence` reads them. */
  review_reasons?: readonly string[] | null;
}

/** A return entered on an order, which settles a credit memo's task. */
export interface TaskMaterialReturn {
  material_order_id: string | null;
  created_at: string;
}

/** A row of `public.tasks` — a typed task, or an answer to a computed one. */
export interface TaskRow {
  id: string;
  title: string;
  detail: string | null;
  job_id: string | null;
  assigned_to: string;
  assigned_by: string | null;
  due_on: string | null;
  source_key: string | null;
  snoozed_until: string | null;
  done_at: string | null;
  done_by: string | null;
  created_at: string;
}

export interface TaskInput {
  /** The person whose list this is. */
  profileId: string;
  jobs: readonly TaskJob[];
  proposals?: readonly TaskProposal[];
  mentions?: readonly TaskMention[];
  materialOrders?: readonly TaskMaterialOrder[];
  workOrders?: readonly TaskWorkOrder[];
  /** Each job's "Material" checklist step, where it has one. */
  materialSteps?: readonly TaskMaterialStep[];
  invoices?: readonly TaskInvoice[];
  /** Customers' replies, read. */
  readings?: readonly TaskReading[];
  /** Calls logged on these jobs, which settle a call asked for. */
  calls?: readonly TaskCall[];
  /** Money recorded on these jobs, which settles "says they have paid". */
  payments?: readonly TaskPayment[];
  /** Email we sent on these jobs, which settles a reply owed. */
  outbound?: readonly TaskOutbound[];
  receipts?: readonly TaskReceipt[];
  /** Payment plans with a monthly payment late (ADR 0286). */
  latePlans?: readonly TaskLatePlan[];
  /**
   * Estimates with work going ahead and no signature, already worked out by
   * `unsignedWork` (issue #463) — the same findings the job page shows.
   */
  unsignedWork?: readonly UnsignedWork[];
  /** What Jev found comparing each job's conversation with its payment schedule (ADR 0226). */
  termsChecks?: readonly PaymentTermsCheckRow[];
  /**
   * Supplier emails to orders@ waiting for a person, and filed credit memos
   * (ADR 0311). One with no job is everybody's; one on a job is its owner's.
   */
  supplierMail?: readonly TaskSupplierMail[];
  /** Returns entered, which settle a credit memo's task. */
  materialReturns?: readonly TaskMaterialReturn[];
  /** Every row of `tasks` for this person, done and open alike. */
  rows?: readonly TaskRow[];
}

/**
 * How long a proposal sits before it is worth chasing.
 *
 * Not the next morning. A customer who has had the quote overnight is thinking
 * about it, and a rep who is nagged about every proposal the day after it goes
 * out stops reading the list.
 */
export const PROPOSAL_CHASE_DAYS = 3;

/** How far ahead an inspection counts as "coming up". Today and tomorrow. */
export const INSPECTION_LOOKAHEAD_DAYS = 1;

/**
 * How far ahead a crew start makes an open material question urgent. A week:
 * long enough to order and take delivery, short enough that the list is not
 * shouting about work a month out.
 */
export const MATERIAL_LOOKAHEAD_DAYS = 7;

/** Stages where material still has to be bought for the work. */
const ORDERING_STAGES: readonly JobStage[] = ["signed", "in_production"];

/** Past `not_placed`, or stamped as ordered: somebody placed it. */
export function orderWasPlaced(order: TaskMaterialOrder): boolean {
  if (order.ordered_at) return true;
  const status = order.status ?? "not_placed";
  return status !== "not_placed";
}

const DAY_MS = 86_400_000;

/** The calendar date `days` after `date`, as `YYYY-MM-DD`. */
function addDays(date: string, days: number): string {
  const at = new Date(`${date}T00:00:00Z`);
  at.setUTCDate(at.getUTCDate() + days);
  return at.toISOString().slice(0, 10);
}

/**
 * The calendar date a timestamp falls on, where the reader is standing.
 *
 * A date column arrives as "2026-09-01" and carries no time and no zone, so it
 * is already the answer. A timestamp has to be converted, and `new Date` reads
 * a bare date as midnight UTC — the evening before, in every American timezone
 * — which is exactly the trap that made "Signed this month" read zero.
 */
export function calendarDay(value: string | null | undefined): string | null {
  if (!value) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const at = new Date(value);
  if (Number.isNaN(at.getTime())) return null;
  return localDateString(at);
}

/** Whole days between two calendar dates. Negative when `from` is later. */
function daysBetween(from: string, to: string): number {
  return Math.round(
    (new Date(`${to}T00:00:00Z`).getTime() -
      new Date(`${from}T00:00:00Z`).getTime()) /
      DAY_MS,
  );
}

/** How a customer is named on a task. */
function who(job: Pick<TaskJob, "job_number" | "contact">): string {
  const name = job.contact?.full_name?.trim();
  if (name) return name;
  return job.job_number ? `Job #${job.job_number}` : "this job";
}

/**
 * The key work nobody signed is remembered by: the estimate and every step the
 * warning names, so ticking it or "Dismiss" on the job page settles that
 * estimate as it stands, and it comes back when more happens on it — the crew
 * paid where it said only materials were ordered (ADR 0324).
 */
export function unsignedWorkTaskKey(
  found: Pick<UnsignedWork, "estimateId" | "steps">,
): string {
  return taskKey(
    "work_unsigned",
    `${found.estimateId}:${found.steps.join("+")}`,
  );
}

/**
 * Work nobody signed, as a to-do. The job page's "Dismiss" writes this same
 * answer, so either one hides both, for that person only (ADR 0324).
 */
export function unsignedWorkTask(
  found: UnsignedWork,
  job: Pick<TaskJob, "id" | "job_number" | "contact">,
): Omit<DerivedTask, "weight"> {
  return {
    key: unsignedWorkTaskKey(found),
    kind: "work_unsigned",
    title: `Get “${found.estimateName}” signed — ${who(job)}`,
    detail: "Work is going ahead and nobody has signed it",
    jobId: job.id,
    dueOn: null,
  };
}

/** Does this person own the job in a way that makes its work theirs? */
export function ownsJob(job: TaskJob, profileId: string): boolean {
  return (
    job.rep_id === profileId ||
    job.alt_rep_id === profileId ||
    job.pm_id === profileId ||
    job.coordinator_id === profileId ||
    job.inspector_id === profileId ||
    job.inspector_2_id === profileId
  );
}

/**
 * Is this job sold, however it got there? The AI Team page asks the same
 * question of a go-ahead (ADR 0259).
 */
export function isSold(job: TaskJob): boolean {
  return (
    SOLD_STAGES.includes(job.stage) ||
    Boolean(job.sends_at) ||
    Boolean(job.signed_contract_at)
  );
}

/** A job nothing should be said about: a practice row, or one written off. */
function ignorable(job: TaskJob): boolean {
  return isTestJob(job) || job.stage === "lost" || job.stage === "closed";
}

/**
 * The kinds that come back, where ticking means "not today" and not "never".
 *
 * Most items name something that happens once: a proposal is chased, an order
 * is placed, a note tagged you on Tuesday morning. Ticking one settles it for
 * good, and the key is the thing's own id, so if it ever happens again it is a
 * different item anyway.
 *
 * These three are not like that. They are a *condition* on a job — nobody has
 * rung this customer, the follow-up date has passed, the inspection is
 * tomorrow — keyed by the job, because the job is the only thing there is to
 * key them by. So the key is the same key today, next week and next month, and
 * a permanent suppression on it means a rep who ticks "Call Barry" once is
 * never told to call Barry again, however long it has been. That is the one
 * failure a list like this cannot have: the item it silently stopped showing
 * is the one nobody will notice is missing.
 *
 * Ticking one therefore lasts the day. If the rep actually made the call, the
 * contact is logged and the condition is gone on its own; if they did not, it
 * is owed again tomorrow, which is the truth. Snoozing is untouched — that one
 * carries its own date and means exactly what it says.
 */
const RECURRING: ReadonlySet<TaskKind> = new Set<TaskKind>([
  "call_overdue",
  "follow_up_due",
  "inspection_soon",
]);

/** The kind a stored `source_key` was built from, if it is still a kind. */
function keyKind(key: string): TaskKind | null {
  const kind = key.slice(0, key.indexOf(":"));
  return kind in WEIGHT ? (kind as TaskKind) : null;
}

/**
 * What a person is not being shown, and why.
 *
 * A row in `tasks` carrying a `source_key` is somebody's answer to a computed
 * item: ticked, or put off until a date. Both hide it — the second only until
 * that date arrives, and the first only for the day on the kinds that recur.
 */
function suppressedKeys(
  rows: readonly TaskRow[],
  today: string,
): ReadonlySet<string> {
  const hidden = new Set<string>();
  for (const row of rows) {
    if (!row.source_key) continue;
    if (row.done_at) {
      const kind = keyKind(row.source_key);
      const lastsTheDay = kind !== null && RECURRING.has(kind);
      if (!lastsTheDay || calendarDay(row.done_at) === today) {
        hidden.add(row.source_key);
      }
      continue;
    }
    if (row.snoozed_until && row.snoozed_until > today) {
      hidden.add(row.source_key);
    }
  }
  return hidden;
}

/** The key a computed item is remembered by. */
export function taskKey(kind: TaskKind, id: string): string {
  return `${kind}:${id}`;
}

/**
 * Everything this person owes, that the app can work out on its own.
 *
 * Ordered: overdue first, then by kind. Anything a person has already ticked or
 * snoozed is left out.
 */
export function derivedTasks(input: TaskInput, now: Date): DerivedTask[] {
  const today = localDateString(now);
  const hidden = suppressedKeys(input.rows ?? [], today);
  const me = input.profileId;
  const out: DerivedTask[] = [];

  const push = (task: Omit<DerivedTask, "weight">) => {
    if (hidden.has(task.key)) return;
    out.push({ ...task, weight: WEIGHT[task.kind] });
  };

  const mine = input.jobs.filter((job) => !ignorable(job) && ownsJob(job, me));
  const byId = new Map(mine.map((job) => [job.id, job]));

  for (const job of mine) {
    const sold = isSold(job);

    // --- Calls and replies owed ------------------------------------------
    const clock = followUpStatus(job, now);
    if (!sold && clock.level === "alert") {
      push({
        key: taskKey("call_overdue", job.id),
        kind: "call_overdue",
        title: `Call ${who(job)}`,
        detail: clock.everContacted
          ? `Nothing logged for ${humanGap(clock.hours)}`
          : "Never called",
        jobId: job.id,
        dueOn: today,
      });
    }

    // A date the rep set themselves, which until now nothing has ever
    // reminded anybody of.
    const followUp = calendarDay(job.follow_up_at);
    if (!sold && followUp && followUp <= today) {
      push({
        key: taskKey("follow_up_due", job.id),
        kind: "follow_up_due",
        title: `Follow up with ${who(job)}`,
        detail:
          followUp === today
            ? "You set this for today"
            : `You set this for ${followUp}`,
        jobId: job.id,
        dueOn: followUp,
      });
    }

    // --- Dates and jobs ---------------------------------------------------
    const inspection = calendarDay(job.inspection_at);
    if (
      inspection &&
      inspection >= today &&
      inspection <= addDays(today, INSPECTION_LOOKAHEAD_DAYS)
    ) {
      push({
        key: taskKey("inspection_soon", job.id),
        kind: "inspection_soon",
        title: `Inspection — ${who(job)}`,
        detail: inspection === today ? "Today" : "Tomorrow",
        jobId: job.id,
        dueOn: inspection,
      });
    }

    // --- Data that is wrong -----------------------------------------------
    // Sold, and nobody wrote down when. It counts in no month's figures and
    // nothing else will ever notice.
    if (sold && !job.signed_contract_at) {
      push({
        key: taskKey("sold_without_signed_date", job.id),
        kind: "sold_without_signed_date",
        title: `Add the signed date for ${who(job)}`,
        detail: "Sold, but no signed date — it counts in no month",
        jobId: job.id,
        dueOn: null,
      });
    }

    // Signed, and still sitting on the prospect side of the board.
    if (job.signed_contract_at && !SOLD_STAGES.includes(job.stage)) {
      push({
        key: taskKey("signed_but_wrong_stage", job.id),
        kind: "signed_but_wrong_stage",
        title: `Move ${who(job)} out of ${job.stage.replace(/_/g, " ")}`,
        detail: "Signed, but the stage never moved",
        jobId: job.id,
        dueOn: null,
      });
    }
  }

  // --- Money and paperwork ------------------------------------------------
  for (const proposal of input.proposals ?? []) {
    const job = byId.get(proposal.job_id);
    if (!job || job.signed_contract_at) continue;
    if (proposal.status !== "sent" && proposal.status !== "viewed") continue;
    const sent = calendarDay(proposal.sent_at);
    if (!sent || daysBetween(sent, today) < PROPOSAL_CHASE_DAYS) continue;

    // "Opened it and hasn't signed" is a different conversation from "never
    // opened it", and the app knows which.
    const opened = proposal.status === "viewed" || Boolean(proposal.viewed_at);
    push({
      key: taskKey("proposal_unsigned", proposal.id),
      kind: "proposal_unsigned",
      title: `Chase ${who(job)} for a signature`,
      detail: opened
        ? `Opened it, not signed — sent ${daysBetween(sent, today)} days ago`
        : `Not opened yet — sent ${daysBetween(sent, today)} days ago`,
      jobId: job.id,
      dueOn: null,
    });
  }

  for (const task of materialTasks(input, mine, today)) push(task);

  // Work under way that the customer never signed for (issue #463).
  for (const found of input.unsignedWork ?? []) {
    const job = byId.get(found.jobId);
    if (!job) continue;
    push(unsignedWorkTask(found, job));
  }

  // Payment terms promised that the contract does not show (ADR 0226). Keyed
  // by what the warning is about, the quoted message and the payment
  // schedules (ADR 0258), so ticking it lasts until one of those changes;
  // "This is fine" on the job hides it the same way.
  for (const row of input.termsChecks ?? []) {
    const job = byId.get(row.job_id);
    const warning = paymentTermsWarning(row);
    if (!job || !warning) continue;
    push({
      key: taskKey("terms_not_in_contract", `${job.id}:${warning.fingerprint}`),
      kind: "terms_not_in_contract",
      title: `Check the payment terms for ${who(job)}`,
      detail: warning.quote
        ? `“${warning.quote.length > 88 ? `${warning.quote.slice(0, 87)}…` : warning.quote}”`
        : "Promised terms the contract doesn’t show",
      jobId: job.id,
      dueOn: null,
    });
  }

  for (const invoice of input.invoices ?? []) {
    const job = byId.get(invoice.job_id);
    if (!job || invoice.status !== "ready") continue;
    push({
      key: taskKey("invoice_not_sent", invoice.id),
      kind: "invoice_not_sent",
      title: `Send the invoice for ${who(job)}`,
      detail: "Prepared, never sent",
      jobId: job.id,
      dueOn: null,
    });
  }

  /*
   * A receipt waiting to be read.
   *
   * Every one of these has a customer at the other end who handed over a check
   * or cash and has heard nothing back. It is not sent by itself any more
   * (ADR 0140), so somebody has to be told it is there — and the person whose
   * job it is, is the person whose list this is.
   */
  for (const receipt of input.receipts ?? []) {
    const job = byId.get(receipt.job_id);
    if (!job) continue;
    push({
      key: taskKey("receipt_to_send", receipt.id),
      kind: "receipt_to_send",
      title: `Send ${who(job)} their receipt`,
      detail: `${formatMoney(cents(Number(receipt.amount_cents)))} ${receipt.method}, waiting to be read`,
      jobId: job.id,
      // The day it was taken. A receipt is owed the same day, and one from
      // last week should read as late rather than as new.
      dueOn: calendarDay(receipt.receipt_waiting_since),
    });
  }

  /*
   * A monthly payment on an in-house plan that has not come.
   *
   * Jonathon, 2026-10-14, asked what happens when a check is late: tell the
   * office, and the customer hears nothing automatically. Keyed by the month
   * it is about, so ticking it after a call answers that month only, and a
   * second missed month asks again.
   */
  for (const late of input.latePlans ?? []) {
    const job = byId.get(late.job_id);
    if (!job) continue;
    push({
      key: taskKey("plan_payment_late", `${late.job_id}:${late.overdue_since}`),
      kind: "plan_payment_late",
      title: `Call ${who(job)}: payment plan check is late`,
      detail: `${formatMoney(cents(Number(late.behind_cents)))} was due ${late.overdue_since} and has not been recorded`,
      jobId: job.id,
      dueOn: late.overdue_since,
    });
  }

  // A production job with nobody booked. Counted per job rather than per work
  // order: the question is whether anyone is coming, not which trade.
  const booked = new Set<string>();
  const hasOrders = new Set<string>();
  for (const order of input.workOrders ?? []) {
    if (order.is_live === false) continue;
    hasOrders.add(order.job_id);
    if (order.scheduled_at) booked.add(order.job_id);
  }
  for (const job of mine) {
    if (job.stage !== "in_production") continue;
    if (booked.has(job.id)) continue;
    push({
      key: taskKey("no_crew_booked", job.id),
      kind: "no_crew_booked",
      title: `Book the work for ${who(job)}`,
      detail: hasOrders.has(job.id)
        ? "Work orders exist, none is scheduled"
        : "In production with no work orders",
      jobId: job.id,
      dueOn: null,
    });
  }

  // --- Tagged in a note ----------------------------------------------------
  // The GIN index on activities.mentions was added for exactly this question
  // and nothing has ever asked it.
  for (const note of input.mentions ?? []) {
    if (!note.mentions?.includes(me)) continue;
    const job = note.job_id ? byId.get(note.job_id) : null;
    const author = note.author?.full_name?.trim() ?? "Somebody";
    const body = (note.body ?? "").replace(/\s+/g, " ").trim();
    push({
      key: taskKey("tagged_in_note", note.id),
      kind: "tagged_in_note",
      title: job
        ? `${author} tagged you on ${who(job)}`
        : `${author} tagged you`,
      detail: body.length > 90 ? `${body.slice(0, 89)}…` : body || "In a note",
      jobId: note.job_id,
      dueOn: calendarDay(note.occurred_at),
    });
  }

  // --- What a customer said -----------------------------------------------
  for (const task of replyTasks(input, byId)) push(task);

  // --- What a supplier sent to orders@ (ADR 0311) -------------------------
  for (const task of supplierMailTasks(input, byId)) push(task);

  return sortTasks(out, today);
}

/**
 * The tasks supplier mail raises.
 *
 * An email the app could not place waits for whoever reads the list: on the
 * job's owner's list when the job is known, and on everybody's when it is not,
 * because an unplaced invoice is nobody's until somebody takes it. A credit
 * memo filed on an order asks for the return to be entered (ADR 0244 — a
 * return is typed by a person), until a return appears on that order after
 * the memo arrived or somebody ticks it.
 */
export function supplierMailTasks(
  input: Pick<
    TaskInput,
    "supplierMail" | "materialReturns" | "jobs" | "profileId"
  >,
  byId: ReadonlyMap<string, TaskJob>,
): Omit<DerivedTask, "weight">[] {
  const out: Omit<DerivedTask, "weight">[] = [];
  for (const mail of input.supplierMail ?? []) {
    const job = mail.job_id ? byId.get(mail.job_id) : null;
    // On a job this person does not own: somebody else's list.
    if (mail.job_id && !job) continue;
    const task =
      mail.status === "review"
        ? reviewTask(mail, job ?? null)
        : creditMemoTask(mail, job ?? null, input.materialReturns ?? []);
    if (task) out.push(task);
  }
  return out;
}

const mailSender = (mail: TaskSupplierMail): string =>
  mail.from_name?.trim() || mail.from_address?.trim() || "a supplier";

const mailSubject = (mail: TaskSupplierMail): string =>
  (mail.subject ?? "").replace(/\s+/g, " ").trim() || "(no subject)";

/** An email the app could not place: somebody has to. */
function reviewTask(
  mail: TaskSupplierMail,
  job: TaskJob | null,
): Omit<DerivedTask, "weight"> {
  const reasons = (mail.review_reasons ?? []).join(" ");
  const subject = mailSubject(mail);
  return {
    key: taskKey("supplier_mail_review", mail.id),
    kind: "supplier_mail_review",
    title: job
      ? `File the email from ${mailSender(mail)} on ${who(job)}`
      : `Place the email from ${mailSender(mail)}`,
    detail: reasons ? `${subject} — ${reasons}` : subject,
    jobId: mail.job_id,
    dueOn: calendarDay(mail.sent_at),
  };
}

/**
 * A filed credit memo whose return has not been typed (ADR 0244), or null
 * once a return appears on that order after the memo arrived.
 */
function creditMemoTask(
  mail: TaskSupplierMail,
  job: TaskJob | null,
  returns: readonly TaskMaterialReturn[],
): Omit<DerivedTask, "weight"> | null {
  if (mail.status !== "filed" || mail.kind !== "credit_memo" || !job)
    return null;
  const entered = returns.some(
    (r) =>
      r.material_order_id !== null &&
      r.material_order_id === mail.material_order_id &&
      r.created_at >= mail.sent_at,
  );
  if (entered) return null;
  const on = mail.order_number ? ` on MO-${mail.order_number}` : "";
  return {
    key: taskKey("supplier_credit_memo", mail.id),
    kind: "supplier_credit_memo",
    title: `Enter the return for ${who(job)}`,
    detail: `Credit memo from ${mailSender(mail)}${on} — type the return and its credit off the memo`,
    jobId: mail.job_id,
    dueOn: calendarDay(mail.sent_at),
  };
}

/** Who wrote, as a task names them: their name, their address, the job. */
function sender(reading: TaskReading, job: TaskJob): string {
  return reading.from_name?.trim() || reading.from_address?.trim() || who(job);
}

/** The customer's words, one line, short enough to sit under a title. */
function quoted(snippet: string | null | undefined): string {
  const text = (snippet ?? "").replace(/\s+/g, " ").trim();
  if (text === "") return "Replied by email";
  return `“${text.length > 88 ? `${text.slice(0, 87)}…` : text}”`;
}

/**
 * The task a reading raises, or null for none.
 *
 * The words are ours and follow the house rule on confidence: above 0.9 the
 * list says what the customer did, from 0.5 it says it "looks like" they did,
 * and below that it says only that a reply is owed. Jev's reading is never the
 * only thing on the line — the customer's own words are quoted under it.
 *
 * A model's "needs nothing" never hides a customer message. It creates a
 * review task at every confidence; a person or normal settlement evidence
 * must clear it (ADR 0210).
 */
export function replyTask(
  reading: TaskReading,
  job: TaskJob,
): Omit<DerivedTask, "weight"> | null {
  const certainty = replyCertainty(reading.probability);
  const name = sender(reading, job);
  const base = {
    detail: quoted(reading.snippet),
    jobId: job.id,
    dueOn: calendarDay(reading.sent_at),
  };
  const owed = (): Omit<DerivedTask, "weight"> => ({
    ...base,
    key: taskKey("reply_owed", reading.message_id),
    kind: "reply_owed",
    title: `Reply to ${name}`,
  });

  if (reading.sender_kind === "staff" || reading.sender_kind === "automated") {
    return null;
  }
  if (reading.sender_kind === "supplier") {
    return {
      ...base,
      key: taskKey("supplier_reply", reading.message_id),
      kind: "supplier_reply",
      title: `Review supplier reply from ${name}`,
    };
  }
  if (reading.sender_kind === "unknown") {
    return {
      ...owed(),
      title: `Review sender and reply to ${name}`,
    };
  }

  if (reading.meaning === "no_action") {
    return { ...owed(), title: `Review message from ${name}` };
  }
  if (reading.meaning === "unclear" || certainty === "unsure") return owed();
  const likely = certainty === "likely";

  const said = (
    kind: TaskKind,
    stated: string,
    looks: string,
  ): Omit<DerivedTask, "weight"> => ({
    ...base,
    key: taskKey(kind, reading.message_id),
    kind,
    title: likely ? `Looks like ${looks}` : stated,
  });

  switch (reading.meaning) {
    case "problem":
      return said(
        "customer_urgent",
        `${name} reported a problem`,
        `${name} has a problem`,
      );
    case "wants_call":
      return said(
        "customer_urgent",
        `${name} asked for a call`,
        `${name} wants a call`,
      );
    case "accepted":
      return said(
        "customer_accepted",
        `${name} said go ahead`,
        `${name} said go ahead`,
      );
    case "paid":
      return said(
        "customer_paid",
        `${name} reported a payment or said one is coming`,
        `${name} reported a payment or said one is coming`,
      );
    case "question":
      return said(
        "reply_owed",
        `${name} asked a question`,
        `${name} asked a question`,
      );
  }
}

/**
 * The kinds that talking to the customer settles.
 *
 * A reply owed, a call asked for and a problem reported are all answered by
 * reaching the person: an email back to them, or a call logged on the job.
 * A go-ahead and a payment are not — writing "great, thanks" does not sign the
 * job or bank the check — so each waits for its own evidence instead.
 */
const SETTLED_BY_TALKING: ReadonlySet<TaskKind> = new Set<TaskKind>([
  "customer_urgent",
  "reply_owed",
  "supplier_reply",
]);

const time = (value: string): number => new Date(value).getTime();

/**
 * Did we write back to this reply?
 *
 * On the same thread, whoever it went to — that is the conversation. Or on
 * another thread of the same job, if it went to the address the reply came
 * from: a rep who starts a fresh email to the customer has answered them as
 * surely as one who pressed Reply. An email on the same job to somebody else,
 * a supplier or a crew, answers nobody.
 */
function emailedBack(
  reading: TaskReading,
  outbound: readonly TaskOutbound[],
): boolean {
  const after = time(reading.sent_at);
  const from = reading.from_address?.trim().toLowerCase() ?? "";
  return outbound.some((message) => {
    if (time(message.sent_at) <= after) return false;
    if (message.thread_id === reading.thread_id) return true;
    return (
      from !== "" &&
      message.job_id != null &&
      message.job_id === reading.job_id &&
      (message.to_addresses ?? []).some(
        (to) => to.trim().toLowerCase() === from,
      )
    );
  });
}

/** Has something happened since the reply that answers this task? */
function settled(
  task: Omit<DerivedTask, "weight">,
  reading: TaskReading,
  job: TaskJob,
  input: TaskInput,
): boolean {
  const after = time(reading.sent_at);

  if (SETTLED_BY_TALKING.has(task.kind)) {
    return (
      emailedBack(reading, input.outbound ?? []) ||
      (input.calls ?? []).some(
        (call) => call.job_id === job.id && time(call.occurred_at) > after,
      )
    );
  }
  if (task.kind === "customer_paid") {
    return (input.payments ?? []).some(
      (payment) => payment.job_id === job.id && time(payment.at) > after,
    );
  }
  if (task.kind === "customer_accepted") return isSold(job);
  return false;
}

/**
 * Tasks from customers' replies.
 *
 * One per thread and kind, from the newest reply of that kind: a customer who
 * asks for a call twice is one call owed, not two. Different kinds on one
 * thread are different things to do and each stands. Each leaves the list on
 * its own once `settled` sees the answer, or when somebody ticks it.
 */
function replyTasks(
  input: TaskInput,
  mine: ReadonlyMap<string, TaskJob>,
): Omit<DerivedTask, "weight">[] {
  // One customer email seen in two mailboxes is two readings of one message.
  // It is answered if either copy is, and otherwise listed once, under the
  // copy with the lowest id so the task — and a tick on it — stays put.
  const copy = (reading: TaskReading): string | null =>
    reading.rfc822_message_id?.trim() || null;
  const candidates: {
    reading: TaskReading;
    task: Omit<DerivedTask, "weight">;
    at: number;
  }[] = [];
  const answered = new Set<string>();
  // Dismissed on the AI Team page (ADR 0259): no task, whatever it meant.
  const live = (input.readings ?? []).filter((reading) => !reading.dismissed);
  for (const reading of live) {
    const job = reading.job_id ? mine.get(reading.job_id) : undefined;
    if (!job) continue;
    const at = time(reading.sent_at);
    if (!Number.isFinite(at)) continue;
    const task = replyTask(reading, job);
    if (!task) continue;
    if (settled(task, reading, job, input)) {
      const id = copy(reading);
      if (id) answered.add(`${id}:${task.kind}`);
      continue;
    }
    candidates.push({ reading, task, at });
  }
  candidates.sort((a, b) =>
    a.reading.message_id.localeCompare(b.reading.message_id),
  );

  const listed = new Set<string>();
  const newest = new Map<
    string,
    { task: Omit<DerivedTask, "weight">; at: number }
  >();
  for (const { reading, task, at } of candidates) {
    const id = copy(reading);
    if (id) {
      const key = `${id}:${task.kind}`;
      if (answered.has(key) || listed.has(key)) continue;
      listed.add(key);
    }
    const slot = `${reading.thread_id}:${task.kind}`;
    const held = newest.get(slot);
    if (!held || at > held.at) newest.set(slot, { task, at });
  }
  return [...newest.values()].map(({ task }) => task);
}

/**
 * What is owed on material, per job (the procurement plan's first delivery).
 *
 * Only jobs signed or in production — a finished job needs nothing bought —
 * and only orders on the active estimate: an alternative that was never sold
 * is not a purchase anybody forgot. An order counts as placed once its status
 * moves past `not_placed`, stamped date or not, so a picked-up order is never
 * reported as unplaced. A "Material" step ticked done or marked N/A settles
 * the job either way.
 *
 * Two answers, never both for one job:
 *
 *   - live orders never placed: one task naming how many, keyed by the orders
 *     themselves, so a new order is a new task and a placed one drops out;
 *   - no live order at all: a question, "confirm whether it needs one". A
 *     repair is not assumed to need nothing — four of the five sold jobs
 *     without an order on 2026-09-18 were repairs, and the only one starting
 *     within the month was one of them.
 *
 * Either becomes due today once a live work order starts within
 * `MATERIAL_LOOKAHEAD_DAYS`, or has already started.
 */
function materialTasks(
  input: TaskInput,
  mine: readonly TaskJob[],
  today: string,
): Omit<DerivedTask, "weight">[] {
  const settledSteps = new Set<string>();
  const openSteps = new Set<string>();
  for (const step of input.materialSteps ?? []) {
    if (step.status === "done" || step.status === "na") {
      settledSteps.add(step.job_id);
    } else openSteps.add(step.job_id);
  }
  // A job with the step both settled and open — two rows — is not settled.
  for (const id of openSteps) settledSteps.delete(id);

  const live = new Map<string, TaskMaterialOrder[]>();
  for (const order of input.materialOrders ?? []) {
    if (order.is_live === false) continue;
    const list = live.get(order.job_id) ?? [];
    list.push(order);
    live.set(order.job_id, list);
  }

  const firstStart = new Map<string, string>();
  for (const order of input.workOrders ?? []) {
    if (order.is_live === false) continue;
    const start = calendarDay(order.start_at);
    if (!start) continue;
    const held = firstStart.get(order.job_id);
    if (!held || start < held) firstStart.set(order.job_id, start);
  }

  const out: Omit<DerivedTask, "weight">[] = [];
  for (const job of mine) {
    if (!ORDERING_STAGES.includes(job.stage)) continue;
    if (settledSteps.has(job.id)) continue;

    const start = firstStart.get(job.id) ?? null;
    const soon =
      start !== null && start <= addDays(today, MATERIAL_LOOKAHEAD_DAYS);
    const when = start
      ? start < today
        ? `work started ${start}`
        : start === today
          ? "work starts today"
          : `work starts ${start}`
      : null;
    const dueOn = soon ? today : null;

    const orders = live.get(job.id) ?? [];
    if (orders.length === 0) {
      out.push({
        key: taskKey("materials_unconfirmed", job.id),
        kind: "materials_unconfirmed",
        title: `Confirm whether ${who(job)} needs a material order`,
        detail: when
          ? `No material order on the active estimate — ${when}`
          : "No material order on the active estimate",
        jobId: job.id,
        dueOn,
      });
      continue;
    }

    const unplaced = orders
      .filter((order) => !order.hold_order && !orderWasPlaced(order))
      .sort((a, b) => a.id.localeCompare(b.id));
    if (unplaced.length === 0) continue;
    const named =
      unplaced.length === 1
        ? unplaced[0]!.order_number
          ? `Order MO-${unplaced[0]!.order_number} has never been placed`
          : "Built, never placed"
        : `${unplaced.length} orders have never been placed`;
    out.push({
      key: taskKey("order_not_placed", unplaced.map((o) => o.id).join("+")),
      kind: "order_not_placed",
      title: `Place the material order for ${who(job)}`,
      detail: when ? `${named} — ${when}` : named,
      jobId: job.id,
      dueOn,
    });
  }
  return out;
}

/**
 * Overdue first, then today, then everything with no date — and within each,
 * the kind that matters most.
 */
export function sortTasks(
  tasks: readonly DerivedTask[],
  today: string,
): DerivedTask[] {
  const band = (task: DerivedTask): number => {
    if (!task.dueOn) return 2;
    if (task.dueOn < today) return 0;
    if (task.dueOn === today) return 1;
    return 2;
  };
  return [...tasks].sort(
    (a, b) =>
      band(a) - band(b) ||
      a.weight - b.weight ||
      (a.dueOn ?? "").localeCompare(b.dueOn ?? "") ||
      a.title.localeCompare(b.title),
  );
}

/** Is this owed now rather than whenever? */
export function isDue(task: { dueOn: string | null }, today: string): boolean {
  return Boolean(task.dueOn && task.dueOn <= today);
}

/**
 * The number on the button.
 *
 * Only what is due today or overdue. The whole list would be a frightening
 * number that never goes down, and a badge nobody can ever clear is a badge
 * people stop seeing.
 */
export function taskBadgeCount(
  derived: readonly DerivedTask[],
  rows: readonly TaskRow[],
  today: string,
): number {
  const open = rows.filter(
    (row) =>
      !row.done_at &&
      !row.source_key &&
      (!row.snoozed_until || row.snoozed_until <= today),
  );
  return (
    derived.filter((task) => isDue(task, today)).length +
    open.filter((row) => isDue({ dueOn: row.due_on }, today)).length
  );
}
